import type { TFunction } from 'i18next';
import {
  ElementFrame,
  SlideChartData,
  SlideChartType,
  SlideComponent,
  SlideComponentType,
  SlideLayout,
  SlideTableData,
} from './slides';
import { appTheme } from '../styles/theme';

export const COMPONENT_CATALOG: ReadonlyArray<{
  type: SlideComponentType;
  label: string;
  description: string;
}> = [
  { type: 'rectangle', label: 'Rectangle', description: 'Solid shape block' },
  { type: 'ellipse', label: 'Circle', description: 'Ellipse or circle' },
  { type: 'line', label: 'Line', description: 'Horizontal divider' },
  { type: 'image', label: 'Image', description: 'Upload or URL' },
  { type: 'text', label: 'Text', description: 'Free text box' },
  { type: 'table', label: 'Table', description: 'Data table with headers' },
  { type: 'chart', label: 'Chart', description: 'Bar, line, or pie chart' },
];

const newComponentId = (): string =>
  `cmp_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;

export const createDefaultTable = (): SlideTableData => ({
  headers: ['Column A', 'Column B', 'Column C'],
  rows: [
    ['Value 1', 'Value 2', 'Value 3'],
    ['Value 4', 'Value 5', 'Value 6'],
  ],
});

export const createDefaultChart = (): SlideChartData => ({
  chartType: 'bar',
  title: 'Quarterly growth',
  labels: ['Q1', 'Q2', 'Q3', 'Q4'],
  values: [42, 58, 71, 65],
});

const normalizeChart = (chart: SlideChartData): SlideChartData => {
  const count = Math.max(chart.labels.length, chart.values.length, 1);
  const labels = [...chart.labels];
  const values = [...chart.values];
  while (labels.length < count) {
    labels.push(`Item ${labels.length + 1}`);
  }
  while (values.length < count) {
    values.push(0);
  }
  return {
    ...chart,
    labels: labels.slice(0, count),
    values: values.slice(0, count).map((value) => (Number.isFinite(value) ? value : 0)),
  };
};

export const updateChartTitle = (chart: SlideChartData, title: string): SlideChartData => ({
  ...normalizeChart(chart),
  title,
});

export const updateChartType = (chart: SlideChartData, chartType: SlideChartType): SlideChartData => ({
  ...normalizeChart(chart),
  chartType,
});

export const updateChartLabel = (
  chart: SlideChartData,
  index: number,
  label: string,
): SlideChartData => {
  const next = normalizeChart(chart);
  next.labels[index] = label;
  return next;
};

export const updateChartValue = (
  chart: SlideChartData,
  index: number,
  value: number,
): SlideChartData => {
  const next = normalizeChart(chart);
  next.values[index] = Number.isFinite(value) ? value : 0;
  return next;
};

export const addChartPoint = (chart: SlideChartData): SlideChartData => {
  const next = normalizeChart(chart);
  next.labels.push(`Item ${next.labels.length + 1}`);
  next.values.push(0);
  return next;
};

export const removeChartPoint = (chart: SlideChartData, index: number): SlideChartData => {
  const next = normalizeChart(chart);
  if (next.labels.length <= 1) {
    return next;
  }
  next.labels.splice(index, 1);
  next.values.splice(index, 1);
  return next;
};

const normalizeTable = (table: SlideTableData): SlideTableData => {
  const colCount = Math.max(1, table.headers.length);
  const headers = table.headers.slice(0, colCount);
  while (headers.length < colCount) {
    headers.push('');
  }

  const rows = table.rows.map((row) => {
    const cells = row.slice(0, colCount);
    while (cells.length < colCount) {
      cells.push('');
    }
    return cells;
  });

  return { headers, rows };
};

export const updateTableHeader = (
  table: SlideTableData,
  index: number,
  value: string,
): SlideTableData => {
  const next = normalizeTable(table);
  next.headers[index] = value;
  return next;
};

export const updateTableCell = (
  table: SlideTableData,
  rowIndex: number,
  colIndex: number,
  value: string,
): SlideTableData => {
  const next = normalizeTable(table);
  next.rows[rowIndex][colIndex] = value;
  return next;
};

export const addTableColumn = (table: SlideTableData): SlideTableData => {
  const next = normalizeTable(table);
  next.headers.push(`Column ${next.headers.length + 1}`);
  next.rows = next.rows.map((row) => [...row, '']);
  return next;
};

export const removeTableColumn = (table: SlideTableData, index: number): SlideTableData => {
  const next = normalizeTable(table);
  if (next.headers.length <= 1) {
    return next;
  }
  next.headers.splice(index, 1);
  next.rows = next.rows.map((row) => row.filter((_, colIndex) => colIndex !== index));
  return next;
};

export const addTableRow = (table: SlideTableData): SlideTableData => {
  const next = normalizeTable(table);
  next.rows.push(next.headers.map(() => ''));
  return next;
};

export const removeTableRow = (table: SlideTableData, index: number): SlideTableData => {
  const next = normalizeTable(table);
  next.rows = next.rows.filter((_, rowIndex) => rowIndex !== index);
  return next;
};

const defaultFrame = (type: SlideComponentType): ElementFrame => {
  switch (type) {
    case 'line':
      return { x: 10, y: 45, w: 80, h: 2 };
    case 'text':
      return { x: 10, y: 12, w: 35, h: 14 };
    case 'table':
      return { x: 8, y: 22, w: 84, h: 38 };
    case 'chart':
      return { x: 10, y: 18, w: 52, h: 42 };
    case 'image':
      return { x: 55, y: 18, w: 32, h: 38 };
    case 'ellipse':
      return { x: 38, y: 28, w: 24, h: 24 };
    default:
      return { x: 30, y: 30, w: 28, h: 22 };
  }
};

export const createSlideComponent = (type: SlideComponentType): SlideComponent => {
  const base = {
    id: newComponentId(),
    type,
    frame: defaultFrame(type),
  };

  switch (type) {
    case 'rectangle':
      return {
        ...base,
        style: {
          fill: appTheme.mint,
          stroke: appTheme.green,
          strokeWidth: 2,
          borderRadius: 8,
          opacity: 1,
        },
      };
    case 'ellipse':
      return {
        ...base,
        style: {
          fill: '#E8F4FC',
          stroke: appTheme.green,
          strokeWidth: 2,
          opacity: 1,
        },
      };
    case 'line':
      return {
        ...base,
        style: {
          fill: appTheme.green,
          stroke: appTheme.green,
          strokeWidth: 0,
          opacity: 1,
        },
      };
    case 'image':
      return {
        ...base,
        imageUrl: 'https://loremflickr.com/800/600/abstract,design',
        imageAlt: 'Slide image',
        style: {
          borderRadius: 12,
          opacity: 1,
        },
      };
    case 'text':
      return {
        ...base,
        text: 'Add your text',
        style: {
          fill: appTheme.text,
          opacity: 1,
        },
      };
    case 'table':
      return {
        ...base,
        table: createDefaultTable(),
        tableStyle: {
          headerFill: appTheme.green,
          headerText: '#ffffff',
          cellFill: appTheme.surface,
          cellText: appTheme.text,
          borderColor: appTheme.border,
        },
        style: {
          opacity: 1,
        },
      };
    case 'chart':
      return {
        ...base,
        chart: createDefaultChart(),
        chartStyle: {
          primaryColor: appTheme.green,
          gridColor: appTheme.border,
          labelColor: appTheme.textMuted,
          showGrid: true,
          showValues: true,
        },
        style: {
          opacity: 1,
        },
      };
    default:
      return base;
  }
};

export const isComponentId = (id: string): boolean => id.startsWith('cmp_');

export const getComponentById = (
  layout: SlideLayout,
  id: string,
): SlideComponent | undefined => layout.components?.find((component) => component.id === id);

export const getComponentLabel = (component: SlideComponent, t?: TFunction): string => {
  const catalog = COMPONENT_CATALOG.find((item) => item.type === component.type);
  const prefix = t
    ? t(`editor.components.catalog.${component.type}`, { defaultValue: catalog?.label ?? 'Component' })
    : catalog?.label ?? 'Component';
  if (component.type === 'text' && component.text) {
    const snippet = component.text.trim().slice(0, 24);
    return snippet ? `${prefix}: ${snippet}` : prefix;
  }
  if (component.type === 'table' && component.table?.headers[0]) {
    const snippet = component.table.headers[0].trim().slice(0, 20);
    return snippet ? `${prefix}: ${snippet}` : prefix;
  }
  if (component.type === 'chart') {
    const snippet = component.chart?.title?.trim().slice(0, 20);
    return snippet ? `${prefix}: ${snippet}` : prefix;
  }
  return prefix;
};

export const addComponentToLayout = (
  layout: SlideLayout,
  type: SlideComponentType,
): { layout: SlideLayout; component: SlideComponent } => {
  const component = createSlideComponent(type);
  const components = [...(layout.components ?? []), component];
  const layerOrder = [...(layout.layerOrder ?? []), component.id];

  return {
    component,
    layout: {
      ...layout,
      components,
      layerOrder,
    },
  };
};

export const updateComponentInLayout = (
  layout: SlideLayout,
  componentId: string,
  changes: Partial<SlideComponent>,
): SlideLayout => ({
  ...layout,
  components: layout.components?.map((component) =>
    component.id === componentId ? { ...component, ...changes } : component,
  ),
});

export const removeComponentFromLayout = (
  layout: SlideLayout,
  componentId: string,
): SlideLayout => ({
  ...layout,
  components: layout.components?.filter((component) => component.id !== componentId),
  layerOrder: layout.layerOrder?.filter((id) => id !== componentId),
});
